import {
  getLastKnownLocationAsString,
  getLastKnownVenueAsString,
} from '@core/NativeServices/Location/LocationService'
import { HasLoggedOut } from '@core/State/UserAccount/userAccountDriver'
import { fullClientVersion } from '@core/Utils/clientVersion'
import { routeApiMigrationTraffic } from './helpers/apiMigrationHelper'

const enum LocalConstants {
  ErrorCodeAppMismatch = 551,
  ErrorCodeUnauthorized = 401,
  ErrorCodeNotFound = 404,
}

export class ApiServiceError extends Error {
  response: Response

  constructor(response: Response) {
    super(`Fetch failed. Error code ${response.status}`)
    this.response = response
  }
}

export function fetchFromApi(
  url: string,
  opts?: RequestInit,
  bypassMigrationRouter?: boolean
): Promise<Response> {
  const headers = new Headers()

  const { headers: optionalHeaders, ...options } = opts || { headers: null }

  if (optionalHeaders) {
    let key: keyof typeof optionalHeaders
    for (key in optionalHeaders) {
      if (typeof key !== 'string') continue
      const value = optionalHeaders[key]
      if (typeof value !== 'string') continue
      headers.append(`${key}`, value)
    }
  }

  // Custom headers can cause CORS check to fail, so only send if URL is relative (same-origin)
  if (isRelativeUrl(url)) {
    addCustomHeaders(headers)
  }

  const routedUrl = bypassMigrationRouter ? url : routeApiMigrationTraffic(url)

  return window
    .fetch(routedUrl, {
      credentials: 'same-origin',
      headers: headers,
      ...options,
    })
    .then(response => {
      if (!response.ok) {
        switch (response.status) {
          case LocalConstants.ErrorCodeAppMismatch: {
            window.location.reload()
            break
          }
          case LocalConstants.ErrorCodeUnauthorized: {
            HasLoggedOut()
            throw new ApiServiceError(response)
          }
          case LocalConstants.ErrorCodeNotFound: {
            // Prevent infinite loop in the case of a legitiate 404
            if (bypassMigrationRouter) throw new ApiServiceError(response)
            return fetchFromApi(url, opts, true).then(fallbackResponse => fallbackResponse)
          }
          default:
            throw new ApiServiceError(response)
        }
      }
      return response
    })
}

export async function get<T>({ url, opts }: { url: string; opts?: RequestInit }): Promise<T> {
  const response = await fetchFromApi(url, opts)
  return response.status === 204 ? (response.text() as unknown as T) : response.json()
}

export async function post<T, TBody = unknown>({
  url,
  body,
  additionalHeaders,
}: {
  url: string
  body?: TBody
  additionalHeaders?: Record<string, string>
}): Promise<T> {
  const response = await fetchFromApi(url, getPostParameters(body, additionalHeaders))
  const contentLength = Number(response.headers.get('content-length') ?? -1)
  if (contentLength === 0) {
    return {} as T
  }
  return response.status === 204 ? (response.text() as unknown as T) : response.json()
}

export async function put<T, TBody = unknown>({
  url,
  body,
}: {
  url: string
  body?: TBody
}): Promise<T> {
  const response = await fetchFromApi(url, getPutParameters(body))
  const contentLength = Number(response.headers.get('content-length') ?? -1)

  if (contentLength === 0) {
    return {} as T
  }

  return response.status === 204 ? (response.text() as unknown as T) : response.json()
}

export async function deleteAsync<T>({ url, body }: { url: string; body?: unknown }): Promise<T> {
  const response = await fetchFromApi(url, getDeleteParameters(body))
  const contentLength = Number(response.headers.get('content-length') ?? -1)
  if (contentLength === 0) {
    return {} as T
  }
  return response.status === 204 ? (response.text() as unknown as T) : response.json()
}

export async function patch<T>({ url, body }: { url: string; body?: unknown }): Promise<T> {
  const response = await fetchFromApi(url, getPatchParameters(body))
  const contentLength = Number(response.headers.get('content-length') ?? -1)
  if (contentLength === 0) {
    return {} as T
  }
  return response.status === 204 ? (response.text() as unknown as T) : response.json()
}

function isRelativeUrl(url: string) {
  const r = new RegExp('^(?:[a-z]+:)?//', 'i')
  return r.test(url) === false
}

function addCustomHeaders(headers: Headers) {
  headers.append('X-TabTouch-Version', fullClientVersion)
  headers.append('X-Location', getLastKnownLocationAsString())
  headers.append('X-Venue', getLastKnownVenueAsString())
}

export function getPostParameters<TRequest>(
  request: TRequest,
  headers?: Record<string, string>
): RequestInit {
  return {
    method: 'POST',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
  }
}

function getPutParameters<TRequest>(request: TRequest): RequestInit {
  return {
    method: 'PUT',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  }
}

function getDeleteParameters<TRequest>(request: TRequest): RequestInit {
  return {
    method: 'DELETE',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  }
}

function getPatchParameters<TRequest>(request: TRequest): RequestInit {
  return {
    method: 'PATCH',
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  }
}

export function isApiServiceError(error: object): error is ApiServiceError {
  return 'response' in error
}
